/*
 * THESIS: the method page is a working showroom; the pyramid is navigated,
 *   the boards are real product frames, and the page refuses the numbered
 *   three-step methodology graphic this category ships.
 * OWN-WORLD: Maestro surfaces under the pyramid scale used structurally:
 *   the pyramid itself is drawn from the 1-19 gradient, lime base (VALUE) to
 *   cyan apex (TRUST); the selected layer owns the reading pane beside it.
 * STORY: a buyer selects layers, sees who governs what, meets the live board
 *   with honest late forecasts, and ends on the Maestro-to-Benjamin loop.
 * FIRST VIEWPORT: kicker + promise headline, then the pyramid figure with
 *   layer buttons and the Strategy pane already open; primary CTA in nav flow.
 * FORM: interactive spine + dossier panes; pinned direction (brief), no seed.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getCtaContent } from '../../content/ctaContent'
import { getHowWeWorkContent } from '../../content/howWeWorkContent'
import { stationHref } from '../../content/canonSlugs'

const TALK_PATH = '/talk?i=discovery'

function useReveal(threshold = 0.1) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return undefined
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

// Layer paint: engineering sits on the lime base, management on the
// gold-orange middle, strategy nearest the cyan apex.
const LAYER_PAINT = {
  strategy: { fill: 'from-pyramid-19/90 to-pyramid-19/55', text: 'text-pyramid-19', ring: 'ring-pyramid-19/60' },
  management: { fill: 'from-pyramid-10/90 to-pyramid-10/55', text: 'text-pyramid-10', ring: 'ring-pyramid-10/60' },
  engineering: { fill: 'from-pyramid-1/90 to-pyramid-1/55', text: 'text-pyramid-1', ring: 'ring-pyramid-1/60' },
}

const PyramidFigure = ({ c, active, onSelect }) => (
  <figure className="mx-auto w-full max-w-md select-none">
    <p className="text-center text-[11px] uppercase tracking-[0.5em] text-pyramid-19/80">{c.apex}</p>
    <div className="mt-2 flex flex-col items-center gap-1.5">
      {c.layers.map((layer, i) => {
        const paint = LAYER_PAINT[layer.id]
        const width = ['w-[46%]', 'w-[71%]', 'w-[96%]'][i]
        const isActive = active === layer.id
        return (
          <button
            key={layer.id}
            type="button"
            onClick={() => onSelect(layer.id)}
            aria-pressed={isActive}
            className={`${width} rounded-md bg-gradient-to-r px-3 py-3.5 text-center transition-all duration-300 ${paint.fill} ${
              isActive
                ? `ring-2 ${paint.ring} scale-[1.02] shadow-lg shadow-black/40`
                : 'opacity-60 hover:opacity-90'
            }`}
            style={{ clipPath: 'polygon(6% 0, 94% 0, 100% 100%, 0 100%)' }}
          >
            <span className="block text-sm font-bold text-[#0d1117]">{layer.name}</span>
          </button>
        )
      })}
    </div>
    <p className="mt-2 text-center text-[11px] uppercase tracking-[0.5em] text-pyramid-1/80">{c.base}</p>
  </figure>
)

const HowWeWorkPage = () => {
  const { i18n } = useTranslation()
  const c = getHowWeWorkContent(i18n.language)
  const bookLabel = getCtaContent(i18n.language).close.book
  const [active, setActive] = useState('strategy')
  const pyramidRef = useReveal(0.08)
  const maestroRef = useReveal(0.05)
  const loopRef = useReveal(0.08)
  const layer = c.pyramid.layers.find((l) => l.id === active)
  const paint = LAYER_PAINT[active]

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/how-we-work" />

      <header className="px-6 pb-6 pt-32 md:px-16 md:pt-40">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
        </div>
      </header>

      {/* ── the pyramid spine ── */}
      <section className="px-6 py-16 md:px-16" id="pyramid">
        <div ref={pyramidRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.35em] text-pyramid-10">{c.pyramid.kicker}</p>
          <h2 className="mt-3 max-w-2xl text-2xl font-light md:text-[2rem]">{c.pyramid.title}</h2>
          <p className="mt-3 max-w-2xl leading-7 text-slate-400">{c.pyramid.body}</p>

          <div className="mt-12 grid items-start gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <PyramidFigure c={c.pyramid} active={active} onSelect={(id) => { setActive(id); trackEvent(`hww_layer_${id}`) }} />

            {/* reading pane for the selected layer */}
            <div key={active} className="pane-swap rounded-2xl border border-white/10 bg-white/[0.02] p-7 md:p-9" aria-live="polite">
              <p className={`text-xs uppercase tracking-[0.35em] ${paint.text}`}>{layer.name}</p>
              <h3 className="mt-2 text-xl font-semibold">{layer.claim}</h3>
              <p className="mt-3 leading-7 text-slate-300">{layer.detail}</p>
              {/* each practice deep-links into its station on the canon descent */}
              <ul className="mt-6 grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {layer.practices.map((p, i) => (
                  <li key={p}>
                    <a
                      href={stationHref(active, i)}
                      onClick={() => trackEvent('hww_practice_click', { practice: p })}
                      className="group flex items-start gap-2.5 text-sm leading-6 text-slate-400 transition-colors hover:text-white"
                    >
                      <span className={`mt-2 h-1 w-1 shrink-0 rounded-full ${paint.text.replace('text-', 'bg-')}`} />
                      <span className="underline decoration-white/0 underline-offset-4 transition group-hover:decoration-white/30">{p}</span>
                      <span className={`opacity-0 transition group-hover:opacity-100 ${paint.text}`} aria-hidden="true">&#8594;</span>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-7 border-t border-white/[0.08] pt-5">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{layer.servicesLabel}</p>
                <div className="mt-3 flex flex-wrap gap-4">
                  {layer.services.map((s) => (
                    <a key={s.href} href={s.href} className={`btn-board ${active === 'strategy' ? 'btn-board-cyan' : active === 'management' ? 'btn-board-orange' : 'btn-board-lime'}`}>
                      {s.label}
                      <span aria-hidden="true">&#8594;</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── Maestro showroom ── */}
      <section className="px-6 py-16 md:px-16" id="maestro">
        <div ref={maestroRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.35em] text-pyramid-19">{c.maestro.kicker}</p>
          <h2 className="mt-3 max-w-2xl text-2xl font-light md:text-[2rem]">{c.maestro.title}</h2>
          <p className="mt-3 max-w-3xl leading-7 text-slate-400">{c.maestro.body}</p>

          {/* the product's own vocabulary, drawn in CSS rather than screenshotted */}
          <div className="mt-8 rounded-xl border border-white/10 bg-[#151b23] p-5">
            <p className="text-sm text-slate-400">{c.maestro.vocabulary.intro}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-[#1a212a] px-3 py-1.5 text-[13px] text-slate-300">
                <span className="h-1.5 w-1.5 rounded-full bg-pyramid-1" />
                {c.maestro.vocabulary.deadlineDone}
              </span>
              <span className="inline-flex items-center gap-2 rounded-md border border-pyramid-10/50 bg-[#1a212a] px-3 py-1.5 text-[13px] font-semibold text-pyramid-10">
                <span className="h-1.5 w-1.5 rounded-full bg-pyramid-10" />
                {c.maestro.vocabulary.deadlineNext}
              </span>
              <span className="inline-flex items-center rounded-full border border-pyramid-19/40 bg-pyramid-19/10 px-3 py-1.5 text-[13px] font-semibold text-pyramid-19">
                {c.maestro.vocabulary.wip}
              </span>
              <span className="inline-flex items-center rounded-md bg-pyramid-10/15 px-3 py-1.5 text-[13px] font-bold uppercase tracking-wide text-pyramid-10">
                {c.maestro.vocabulary.late}
              </span>
            </div>
            <p className="mt-3 text-[13px] leading-6 text-slate-400">{c.maestro.vocabulary.outro}</p>
          </div>

          <div className="mt-10 space-y-12">
            {c.maestro.shots.map((shot, i) => (
              <figure key={shot.src} className={`grid items-center gap-6 lg:grid-cols-12 ${i % 2 ? '' : ''}`}>
                <div className={`min-w-0 lg:col-span-8 ${i % 2 ? 'lg:order-2' : ''}`}>
                  <div className="max-w-full overflow-x-auto">
                    <div className="overflow-hidden rounded-xl bg-[#1a212a] ring-1 ring-white/[0.12] shadow-2xl shadow-black/50">
                      <img src={shot.src} alt={shot.alt} loading="lazy" className="block w-full" />
                    </div>
                  </div>
                </div>
                <figcaption className={`lg:col-span-4 ${i % 2 ? 'lg:order-1 lg:text-right' : ''}`}>
                  <p className="text-sm leading-7 text-slate-300">{shot.caption}</p>
                  {shot.rule ? (
                    <p className="mt-4 border-t border-white/[0.08] pt-3 text-[13px] leading-6 text-slate-400">
                      {shot.rule}
                    </p>
                  ) : null}
                </figcaption>
              </figure>
            ))}
          </div>

          {/* the flagship's full argument lives on its own page */}
          {c.maestro.link ? (
            <div className="mt-12 border-t border-white/[0.08] pt-8">
              <a
                href={c.maestro.link.href}
                onClick={() => trackEvent('hww_maestro_page_click')}
                className="btn-board btn-board-cyan"
              >
                {c.maestro.link.label}
                <span aria-hidden="true">&#8594;</span>
              </a>
            </div>
          ) : null}
        </div>
      </section>

      {/* ── the loop ── */}
      <section className="px-6 py-16 md:px-16" id="loop">
        <div ref={loopRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.35em] text-pyramid-10">{c.loop.kicker}</p>
          <h2 className="mt-3 max-w-3xl text-2xl font-light md:text-[2rem]">{c.loop.title}</h2>
          <p className="mt-3 max-w-3xl leading-7 text-slate-400">{c.loop.body}</p>

          <ol className="stagger-grid is-visible mt-10 grid gap-3 md:grid-cols-5">
            {c.loop.steps.map((step, i) => (
              <li key={step.name} className="stagger-card hover-lift relative rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <p className="text-sm font-bold text-white">{step.name}</p>
                <p className="mt-2 text-[13px] leading-6 text-slate-400">{step.detail}</p>
                {i < c.loop.steps.length - 1 ? (
                  <span className="flow-dash absolute -right-3 top-1/2 hidden w-3 -translate-y-1/2 text-pyramid-10 md:block" aria-hidden="true" />
                ) : null}
              </li>
            ))}
          </ol>
          <p className="mt-6 max-w-3xl text-sm italic leading-6 text-slate-400">{c.loop.close}</p>
        </div>
      </section>

      {/* ── close ── */}
      <section className="px-6 pb-24 pt-8 md:px-16">
        <div className="mx-auto max-w-6xl rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center md:p-12">
          <h2 className="text-2xl font-light md:text-3xl">{c.cta.title}</h2>
          <p className="mx-auto mt-3 max-w-xl leading-7 text-slate-400">{c.cta.body}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href={TALK_PATH}
              onClick={() => trackEvent('hww_talk_click')}
              className="inline-flex items-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-7 py-3 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5"
            >
              {c.cta.label}
              <span aria-hidden="true">&#8594;</span>
            </a>
            <a
              href="/book"
              onClick={() => trackEvent('hww_book_click')}
              className="inline-flex items-center gap-2 rounded-[10px] border border-pyramid-19/50 px-7 py-3 font-semibold text-pyramid-19 transition-all hover:bg-pyramid-19/10"
            >
              {bookLabel}
              <span aria-hidden="true">&#8594;</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HowWeWorkPage
